import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Employee } from '../../../../domain/employees/employee';
import FormErrorMessage from '../../../shared/components/forms/FormErrorMessage';
import ContactInformationSection from './ContactInformationSection';
import OtherInformationSection from './OtherInformationSection';
import PersonalInformationSection from './PersonalInformationSection';
import { CreateEmployee } from '../../../../domain/employees/createEmployee';
import { EmployeesRepository } from '../../../../infrastructure/repositories/employees/employeeRepository';
import { UpdatedEmployee } from '../../../../domain/employees/updateEmployee';
import { Private_Routes } from '../../../../main/constants';
import Swal from 'sweetalert2';

interface Props {
    isNewEmployee: boolean;
    employee?: Employee | null;
}

export const EmployeeForm = ({
    isNewEmployee,
    employee: selectedEmployee,
}: Props) => {
    const initialState = selectedEmployee ?? {
        id: 0,
        documentTypeName: '',
        documentTypeId: 0,
        cityName: '',
        cityId: 0,
        positionName: '',
        positionId: 0,
        groupName: '',
        groupId: 0,
        documentNumber: '',
        firstName: '',
        middleName: '',
        lastName: '',
        surname: '',
        email: '',
        phoneNumber: '',
        cellphoneNumber: '',
        address: '',
        birthDate: null,
        gender: 0,
        hasUser: false,
        role: '',
        isActive: false,
    };

    const [employee, setEmployee] = useState<Employee>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isIncomplete, setIsIncomplete] = useState(true);

    const navigate = useNavigate();

    const handleInputChange = (
        name: string,
        value: string | boolean | number[] | number | undefined | null
    ) => {
        setEmployee({ ...employee, [name]: value });
    };

    const handleError = (error: any) => {
        setErrorMessage(error.response.data);
        setIsSubmitting(false);
        window.scrollTo({ behavior: 'smooth', left: 0, top: 0 });
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        setIsSubmitting(true);
        setErrorMessage('');

        if (isNewEmployee) {
            let body: CreateEmployee = {
                documentTypeId: employee.documentTypeId,
                cityId: employee.cityId,
                positionId: employee.positionId,
                groupId: employee.groupId,
                documentNumber: employee.documentNumber,
                firstName: employee.firstName,
                middleName: employee.middleName,
                lastName: employee.lastName,
                surname: employee.surname,
                email: employee.email,
                phoneNumber: employee.phoneNumber,
                cellphoneNumber: employee.cellphoneNumber,
                address: employee.address,
                birthDate: employee.birthDate === '' ? null : employee.birthDate,
                gender: employee.gender,
                hasUser: employee.hasUser,
                role: employee.role,
            };

            EmployeesRepository.CreateEmployee(body)
                .then((response) => {
                    navigate(Private_Routes.EMPLOYEE_PAGE);
                    setIsSubmitting(false);
                    Swal.fire('Empleado creado exitosamente!', '', 'success');
                })
                .catch(handleError);
        } else {
            let body: UpdatedEmployee = {
                documentTypeId: employee.documentTypeId,
                cityId: employee.cityId,
                positionId: employee.positionId,
                groupId: employee.groupId,
                documentNumber: employee.documentNumber,
                firstName: employee.firstName,
                middleName: employee.middleName,
                lastName: employee.lastName,
                surname: employee.surname,
                email: employee.email,
                phoneNumber: employee.phoneNumber,
                cellphoneNumber: employee.cellphoneNumber,
                address: employee.address,
                birthDate: employee.birthDate === '' ? null : employee.birthDate,
                gender: employee.gender,
                role: employee.role,
                hasUser: employee.hasUser,
                isActive: employee.isActive,
            };

            EmployeesRepository.UpdateEmployee(body, employee.id)
                .then((response) => {
                    navigate(Private_Routes.EMPLOYEE_PAGE);
                    setIsSubmitting(false);
                    Swal.fire('Empleado actualizado exitosamente!', '', 'success');
                })
                .catch(handleError);
        }
    };

    useEffect(() => {
        if (
            employee.documentTypeId > 0 &&
            employee.firstName !== '' &&
            employee.lastName !== '' &&
            employee.email !== '' &&
            employee.cityId > 0 &&
            employee.positionId > 0 &&
            employee.groupId > 0 &&
            employee.documentNumber !== ''
        ) {
            setIsIncomplete(false);
        } else {
            setIsIncomplete(true);
        }
    }, [employee]);

    return (
        <div>
            <FormErrorMessage errorMessage={errorMessage} />
            <div className='row'>
                <div className='col-xxl-12'>
                    <div className='card '>
                        <div className='card-body p-4'>
                            <div className='row'>
                                <PersonalInformationSection
                                    isNewEmployee={isNewEmployee}
                                    employee={employee}
                                    onChange={handleInputChange}
                                />
                                <ContactInformationSection
                                    employee={employee}
                                    onChange={handleInputChange}
                                />
                                <OtherInformationSection
                                    employee={employee}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className='d-flex gap-2'>
                    <Link
                        to={Private_Routes.EMPLOYEE_PAGE}
                        className='btn btn-withe'
                    >
                        <i className='mdi mdi-arrow-left'></i> Atrás
                    </Link>

                    {isNewEmployee ? (
                        <button
                            className='btn btn-primary'
                            disabled={isSubmitting || isIncomplete}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? 'Creando...' : 'Crear'}
                        </button>
                    ) : (
                        <button
                            className='btn btn-primary'
                            disabled={isSubmitting || isIncomplete}
                            onClick={handleSubmit}
                        >
                            <i className='ri-arrow-up-circle-line'></i>
                            {isSubmitting ? 'Actualizando...' : 'Actualizar'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
